import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

import { useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useSession, useUserProfile } from "@pontimed/auth-client";
import {
  FeedbackSheet,
  useGlobalErrorCapture,
  type FeedbackMessages,
  type FeedbackSessionUser,
} from "@pontimed/feedback-ui";
import { cn } from "@pontimed/ui";
import { useRouterState } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";

interface FeedbackContextValue {
  /** Open the feedback sheet. */
  open: () => void;
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

/**
 * Portal feedback widget provider. Mounts a single `<FeedbackSheet>` at the
 * app root in `mode="portal"`, exposes an `open()` trigger via `useFeedback()`.
 *
 * Per `_docs/web-portal-navigation.md` D13:
 *   - identity is forced to "account" (selector hidden)
 *   - small "Sending as <email>" badge replaces selector
 *   - if session unexpectedly missing, package renders a fallback sign-in link
 *
 * Lingui-resolved messages object built once per render via `useLingui().t`
 * template-tag macro. Translation keys are extracted by `lingui:extract` from
 * the literal strings here.
 *
 * `useGlobalErrorCapture()` is wired here so console + network + perf context
 * is available to bug reports submitted from anywhere in the portal.
 */
export function FeedbackProvider({ children }: { children: ReactNode }) {
  const { t } = useLingui();
  const { user: sessionUser } = useSession();
  const { data: profile } = useUserProfile();

  // TanStack Router v1: subscribe to primitive selectors. Destructuring the
  // full `useRouterState()` object and reading `location.pathname` /
  // `location.searchStr` triggers a "Cannot convert object to primitive
  // value" TypeError on this build because the router state is a Valtio-style
  // proxy whose nested `location` lacks a `Symbol.toPrimitive` trap.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });

  // Capture console / network / performance context for bug reports. Single
  // instance per page — provider mounts once at __root.
  useGlobalErrorCapture();

  const [open, setOpen] = useState(false);

  // Hydrate the package's `user` shape from session (userId) + profile (rich).
  // Profile can lag the session (separate query) — fall back to id-only so the
  // sheet stays usable. Defensive `typeof` guards prevent any non-string
  // server payload (e.g. encrypted-blob shapes) from leaking into the package.
  const user: FeedbackSessionUser | null = useMemo(() => {
    if (!sessionUser?.userId || typeof sessionUser.userId !== "string") return null;
    const base: FeedbackSessionUser = { userId: sessionUser.userId };
    if (profile && typeof profile === "object") {
      if (typeof profile.email === "string") base.email = profile.email;
      if (typeof profile.firstName === "string") base.firstName = profile.firstName;
      if (typeof profile.lastName === "string") base.lastName = profile.lastName;
      if (typeof profile.phone === "string") base.phone = profile.phone;
    }
    return base;
  }, [sessionUser, profile]);

  // Build login fallback URL with `?next=` so the package's "Sign in to send
  // feedback" link returns the user to the page they were on. The portal no
  // longer has a `/login` route — the AuthModalProvider auto-opens the
  // modal when it sees `?auth=open` in any URL, so we point the package's
  // anchor at `/find?auth=open&next=...` (any public route would work).
  const loginUrl = useMemo(() => {
    const next = String(pathname ?? "/") + String(searchStr ?? "");
    const params = new URLSearchParams({ auth: "open", next });
    return `/find?${params.toString()}`;
  }, [pathname, searchStr]);

  const messages: FeedbackMessages = useMemo(
    () => ({
      button: t`Send feedback`,
      title: t`Send feedback`,
      close: t`Close`,
      typeQuestion: t`What kind of feedback?`,
      types: {
        bug: t`Bug`,
        idea: t`Idea`,
        question: t`Question`,
        praise: t`Praise`,
        complaint: t`Complaint`,
        incorrect_data: t`Incorrect data`,
      },
      descriptionPlaceholder: t`Tell us what's on your mind…`,
      sendAs: t`Send as`,
      emailPlaceholder: t`your@email.com`,
      identity: {
        // Portal mode renders only `account` + `accountNote` + `user` from
        // this group; `email` / `anonymous` are kept for type completeness
        // (FeedbackMessages shape) but never displayed in portal.
        account: t`Account`,
        email: t`Email`,
        anonymous: t`Anonymous`,
        user: t`User`,
        accountNote: t`Linked to your PontiMed account.`,
      },
      screenshotToggle: t`Attach screenshot`,
      capturingScreenshot: t`Capturing screenshot…`,
      screenshotAttached: t`Screenshot attached`,
      annotateScreenshot: t`Annotate`,
      uploadImages: t`Upload images`,
      maxUploadsReached: t`Maximum 3 images.`,
      invalidImageType: t`Unsupported image type.`,
      imageTooLarge: t`Image too large (max 5 MB).`,
      submit: t`Send`,
      submitting: t`Sending…`,
      thankYou: t`Thank you!`,
      submitted: t`Your feedback has been received.`,
      submitFailed: t`We couldn't send your feedback. Please try again.`,
      rateLimited: t`You've sent several reports in a short time. Please try again later.`,
      // Portal mode has no email option: signing in again is the way out.
      sessionExpired: t`Your session has expired. Sign in again, then send it.`,
      // Portal-only optional keys.
      signInToSendFeedback: t`Sign in to send feedback.`,
      sendingAs: t`Sending as`,
    }),
    [t],
  );

  // Lift the sheet above the mobile bottom-tab-bar (h-14 + safe-area) so the
  // submit CTA stays reachable, mirroring apps/web's account-page treatment.
  const panelClassName = cn(
    "pb-[calc(env(safe-area-inset-bottom)+20px)] md:pb-0",
    "md:bottom-[calc(5.5rem+env(safe-area-inset-bottom))] lg:bottom-16",
  );

  const value = useMemo<FeedbackContextValue>(() => ({ open: () => setOpen(true) }), []);

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <FeedbackSheet
        open={open}
        onOpenChange={setOpen}
        // Always "web" — same selector behaviour as apps/web + legacy:
        //   authenticated → ["account", "email", "anonymous"]
        //   anonymous     → ["email", "anonymous"]
        // (portal mode locked everyone to "account" with selector hidden.)
        mode="web"
        user={user}
        messages={messages}
        apiFetch={apiFetch}
        components={{ ResponsiveSheet, Button, Input }}
        cn={cn}
        loginUrl={loginUrl}
        panelClassName={panelClassName}
        listenForGlobalEvent
      />
    </FeedbackContext.Provider>
  );
}

/**
 * Trigger the portal feedback sheet from anywhere inside <FeedbackProvider>.
 * Returns a no-op `open` if called outside the provider so the build never
 * crashes (rare but possible during pre-_authenticated route resolution).
 */
export function useFeedback(): FeedbackContextValue {
  const ctx = useContext(FeedbackContext);
  if (!ctx) return { open: () => {} };
  return ctx;
}
